// partners/adminView/pages/A9_AdminMilestones.jsx

import { useState, useEffect, useRef } from "react";
import {
  useGetAdminMilestonesQuery,
  useMarkMilestonePaidMutation,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { capturePartnerEvent } from "../../../utils/analytics";

export default function AdminMilestones() {
  const { data, isLoading, refetch } = useGetAdminMilestonesQuery({ paid: "false" });
  const [markPaid, { isLoading: saving }] = useMarkMilestonePaidMutation();
  const [feedback, setFeedback] = useState("");

  const milestones = data?.results || data || [];
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_milestones_viewed");
    }
  }, [isLoading]);

  const handleMarkPaid = async (msId) => {
    if (!window.confirm("Mark this milestone bonus as paid? This cannot be undone.")) return;
    try {
      await markPaid(msId).unwrap();
      setFeedback("Milestone marked as paid.");
      capturePartnerEvent("partner_admin_milestone_marked_paid", { milestone_id: msId, success: true });
      refetch();
    } catch {
      capturePartnerEvent("partner_admin_milestone_marked_paid", { milestone_id: msId, success: false });
      setFeedback("Mark failed.");
    }
  };

  return (
    <AdminLayout>
      <h1 className="font-poppins text-2xl font-bold mb-6">Milestones — Unpaid</h1>
      {feedback && <div className="mb-4 text-sm text-mnf-cyan">{feedback}</div>}

      {isLoading ? <div className="text-mnf-text-muted text-sm">Loading...</div> : (
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-mnf-dark-3 text-mnf-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Partner</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Milestone</th>
                <th className="px-5 py-3 text-left">Bonus</th>
                <th className="px-5 py-3 text-left">Achieved</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {milestones.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-mnf-text-muted">
                    No unpaid milestones. 🎉
                  </td>
                </tr>
              )}
              {milestones.map((ms) => (
                <tr key={ms.id} className="border-t border-white/[0.04] hover:bg-mnf-dark-3 transition-colors">
                  <td className="px-5 py-3 font-semibold">{ms.partner_name || "—"}</td>
                  <td className="px-5 py-3 text-xs text-mnf-text-muted">
                    {ms.partner_type === "creative_ambassador" ? "Ambassador" : "Ecosystem"}
                  </td>
                  <td className="px-5 py-3 text-mnf-text-secondary capitalize">
                    {ms.milestone_type?.replace(/_/g, " ")}
                  </td>
                  <td className="px-5 py-3 text-mnf-cyan font-semibold">
                    ₹{Number(ms.bonus_amount || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-5 py-3 text-xs text-mnf-text-muted">
                    {ms.achieved_at
                      ? new Date(ms.achieved_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => handleMarkPaid(ms.id)}
                      disabled={saving}
                      className="text-xs font-poppins font-semibold text-mnf-cyan border border-mnf-cyan/30 px-3 py-1.5 rounded-mnf hover:bg-mnf-cyan/10 disabled:opacity-50 transition-colors"
                    >
                      Mark paid
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
